import type { IThemeSettings } from '../types';

import { getWallpaperSettingsSignature } from './wallpaperSettingsSignature';

const THEME_WALLPAPER_KEYS: ReadonlyArray<keyof IThemeSettings> = [
  'background',
  'backgroundDocumentId',
  'backgroundColor',
  'patternColor',
  'isBlurred',
  'wallpaperSettings',
  'isPatternWallpaper',
  'isDarkWallpaper',
  'wallpaperMimeType',
  'isWallpaperOverridden',
];

export function pickThemeWallpaperSettings(
  settings?: Partial<IThemeSettings>,
): Partial<IThemeSettings> {
  if (!settings) {
    return {};
  }

  const picked: Partial<IThemeSettings> = {};

  THEME_WALLPAPER_KEYS.forEach((key) => {
    if (key in settings) {
      assignThemeWallpaperSetting(picked, settings, key);
    }
  });

  return picked;
}

export function hasThemeWallpaperMutation(patch?: Partial<IThemeSettings>) {
  if (!patch) {
    return false;
  }

  return THEME_WALLPAPER_KEYS.some((key) => key in patch);
}

export function hasThemeSettingsPatchChanged(
  current?: Partial<IThemeSettings>,
  patch?: Partial<IThemeSettings>,
) {
  if (!patch) {
    return false;
  }

  const patchKeys = Object.keys(patch) as Array<keyof IThemeSettings>;
  if (!patchKeys.length) {
    return false;
  }

  return patchKeys.some((key) => {
    if (key === 'wallpaperSettings') {
      return getWallpaperSettingsSignature(current?.wallpaperSettings)
        !== getWallpaperSettingsSignature(patch.wallpaperSettings);
    }

    return current?.[key] !== patch[key];
  });
}

export function buildThemeWallpaperDebugSnapshot(settings?: Partial<IThemeSettings>) {
  if (!settings) {
    return undefined;
  }

  return {
    background: settings.background,
    backgroundDocumentId: settings.backgroundDocumentId,
    backgroundColor: settings.backgroundColor,
    patternColor: settings.patternColor,
    isBlurred: settings.isBlurred,
    isPatternWallpaper: settings.isPatternWallpaper,
    isDarkWallpaper: settings.isDarkWallpaper,
    wallpaperMimeType: settings.wallpaperMimeType,
    isWallpaperOverridden: settings.isWallpaperOverridden,
    wallpaperSettingsSignature: getWallpaperSettingsSignature(settings.wallpaperSettings),
  };
}

export function hasThemeWallpaperSnapshotChanged(
  previous?: Partial<IThemeSettings>,
  next?: Partial<IThemeSettings>,
) {
  const previousSnapshot = buildThemeWallpaperDebugSnapshot(previous);
  const nextSnapshot = buildThemeWallpaperDebugSnapshot(next);

  if (!previousSnapshot || !nextSnapshot) {
    return previousSnapshot !== nextSnapshot;
  }

  return (
    previousSnapshot.background !== nextSnapshot.background
    || previousSnapshot.backgroundDocumentId !== nextSnapshot.backgroundDocumentId
    || previousSnapshot.backgroundColor !== nextSnapshot.backgroundColor
    || previousSnapshot.patternColor !== nextSnapshot.patternColor
    || previousSnapshot.isBlurred !== nextSnapshot.isBlurred
    || previousSnapshot.isPatternWallpaper !== nextSnapshot.isPatternWallpaper
    || previousSnapshot.isDarkWallpaper !== nextSnapshot.isDarkWallpaper
    || previousSnapshot.wallpaperMimeType !== nextSnapshot.wallpaperMimeType
    || previousSnapshot.isWallpaperOverridden !== nextSnapshot.isWallpaperOverridden
    || previousSnapshot.wallpaperSettingsSignature !== nextSnapshot.wallpaperSettingsSignature
  );
}

function assignThemeWallpaperSetting<K extends keyof IThemeSettings>(
  target: Partial<IThemeSettings>,
  source: Partial<IThemeSettings>,
  key: K,
) {
  target[key] = source[key];
}
